import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useImmer } from "use-immer";
import {
  DocumentView,
  PagelessFillFrame,
  FormFillContextProvider,
  ValidationProvider,
  PreviewFormProvider,
  LiveScoreChip,
  validateDocument,
  type EacoDocumentContent,
  type LocalDocumentResponseSet,
} from "@eaco/forms-ui-web";
import { FormsFileTransportProvider } from "@/lib/FormsFileTransportProvider";
import { PUBLIC_FORM_QUERY } from "@/queries/publicForm";
import { APP_TURNSTILE_SITE_KEY } from "../../config";
import {
  FORM_DOCUMENT_RESULT_QUERY,
  START_FORM_DRAFT,
  SAVE_FORM_DRAFT,
  SUBMIT_FORM_RESULT,
  type FormDocumentResult,
} from "@/queries/formResults";
import {
  clearDraftHandle,
  loadDraftHandle,
  saveDraftHandle,
  type DraftHandle,
} from "@/lib/draftStorage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "sonner";
import NotFoundView from "./NotFoundView";
import ClosedView from "./ClosedView";
import PasswordView from "./PasswordView";
import PoweredByEaco from "./PoweredByEaco";
import TurnstileSubmit from "./TurnstileSubmit";

const AUTOSAVE_DEBOUNCE_MS = 600;

type PublicFormAvailability =
  | "OPEN"
  | "NOT_YET_OPEN"
  | "CLOSED"
  | "UNPUBLISHED";

interface PublicFormResponse {
  publicSlug: string;
  name: string;
  requirePassword: boolean;
  passwordValid: boolean | null;
  updatedAt: string | null;
  versionId: string | null;
  versionNumber: number | null;
  content: EacoDocumentContent | null;
  responseSets: LocalDocumentResponseSet[] | null;
  requireTurnstile: boolean;
  requiredOverride: boolean;
  answerableTypeIds: string[] | null;
  availability: PublicFormAvailability;
  publicStartAt: string | null;
  publicExpiryAt: string | null;
  publicTimezone: string | null;
}

const FormView: React.FC = () => {
  const { publicSlug } = useParams<{ publicSlug: string }>();
  const slug = publicSlug || "";

  // 1. Hydrate any stored draft handle from localStorage. If found,
  //    we attempt to resume by querying the existing FormDocumentResult.
  const [draftHandle, setDraftHandle] = useState<DraftHandle | null>(() =>
    loadDraftHandle(slug)
  );

  // 2. Resume query — only fires when we have a stored handle.
  const {
    data: resultData,
    loading: resultLoading,
    error: resultError,
  } = useQuery<{ formDocumentResult: FormDocumentResult | null }>(
    FORM_DOCUMENT_RESULT_QUERY,
    {
      variables: draftHandle
        ? { id: draftHandle.resultId, secretToken: draftHandle.secretToken }
        : { id: "", secretToken: null },
      skip: !draftHandle || !slug,
      fetchPolicy: "network-only",
    }
  );

  // If the stored handle no longer resolves (token revoked / row
  // pruned), drop it so the next render falls back to publicForm.
  useEffect(() => {
    if (
      draftHandle &&
      !resultLoading &&
      resultData &&
      resultData.formDocumentResult === null
    ) {
      clearDraftHandle(slug);
      setDraftHandle(null);
    }
  }, [draftHandle, resultLoading, resultData, slug]);

  // 3. Fall back to publicForm when there's no resume target. When a
  //    draft IS resumed we read the version content from the result
  //    itself so the filler stays on their original version.
  // Owner-password gate: the submitted attempt rides on the publicForm
  // query itself — the server withholds content until it verifies.
  const [password, setPassword] = useState<string | null>(null);

  const {
    data: publicData,
    loading: publicLoading,
    error: publicError,
  } = useQuery<{ publicForm: PublicFormResponse | null }>(PUBLIC_FORM_QUERY, {
    variables: { publicSlug: slug, password },
    skip: !slug || !!draftHandle,
  });

  const resumedResult = resultData?.formDocumentResult ?? null;
  const publicForm = publicData?.publicForm ?? null;

  // Source of truth for the rendered form, picking between the
  // snapshot stored on the result and the current published version.
  const formName = resumedResult?.formName ?? publicForm?.name ?? "";
  const baseContent: EacoDocumentContent | null =
    (resumedResult?.versionContent as EacoDocumentContent | null) ??
    publicForm?.content ??
    null;
  // The owner's global response sets arrive beside the content (an
  // anonymous filler can't query them by id). Fold them in with the
  // form-local ones so the choice elements resolve both the same way.
  const tenantResponseSets = resumedResult
    ? resumedResult.versionResponseSets
    : publicForm?.responseSets;
  const content = useMemo<EacoDocumentContent | null>(() => {
    if (!baseContent || !tenantResponseSets?.length) return baseContent;
    return {
      ...baseContent,
      responseSets: [
        ...(baseContent.responseSets ?? []),
        ...tenantResponseSets,
      ],
    };
  }, [baseContent, tenantResponseSets]);
  // Form-level "all questions required" override — from the resumed result
  // (its parent form) or the fresh publicForm payload.
  const requiredOverride = !!(
    resumedResult?.requiredOverride ?? publicForm?.requiredOverride
  );
  // Catalog-sourced answerable typeIds (the DB `is_answerable` flag is the
  // source of truth), served on the publicForm payload so requiredOverride
  // recognises every input type without a separate authenticated query.
  const answerableTypeIds = useMemo(
    () =>
      publicForm?.answerableTypeIds
        ? new Set<string>(publicForm.answerableTypeIds)
        : undefined,
    [publicForm?.answerableTypeIds]
  );

  // Reflect the form's name in the browser tab title — falls back to
  // the static "Eaco Forms" set in index.html when no name is loaded.
  useEffect(() => {
    document.title = formName ? `${formName} - Eaco Forms` : "Eaco Forms";
    return () => {
      document.title = "Eaco Forms";
    };
  }, [formName]);

  const loading = draftHandle ? resultLoading : publicLoading;
  const error = draftHandle ? resultError : publicError;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading form…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <p className="text-sm text-destructive">{error.message}</p>
      </div>
    );
  }
  // Public availability window — only meaningful on the fresh publicForm
  // path (a resumed draft carries its own version content). Outside the
  // window the server withholds content, so check this before the
  // not-found fallback below.
  if (
    !draftHandle &&
    publicForm &&
    publicForm.availability &&
    publicForm.availability !== "OPEN"
  ) {
    return (
      <ClosedView
        availability={publicForm.availability}
        startAt={publicForm.publicStartAt}
        timezone={publicForm.publicTimezone}
      />
    );
  }

  // Owner-password gate — content stays withheld server-side until the
  // filler's attempt verifies, so this renders whenever the form is
  // otherwise open but locked.
  if (!draftHandle && publicForm?.requirePassword && !publicForm.content) {
    return (
      <PasswordView
        formName={publicForm.name}
        invalid={publicForm.passwordValid === false}
        checking={publicLoading}
        onSubmit={setPassword}
      />
    );
  }

  if (!content) {
    // No active version (or slug doesn't exist) — indistinguishable
    // from "form not found" on purpose so the anonymous endpoint
    // doesn't leak which slugs are real-but-not-yet-published.
    return <NotFoundView />;
  }

  if (resumedResult?.status === "SUBMITTED") {
    return <AlreadySubmittedView name={formName} />;
  }

  return (
    <FilledForm
      slug={slug}
      formName={formName}
      content={content}
      publicPassword={password}
      requireTurnstile={!!publicForm?.requireTurnstile}
      requiredOverride={requiredOverride}
      answerableTypeIds={answerableTypeIds}
      resumedResult={resumedResult}
      draftHandle={draftHandle}
      onDraftStarted={h => {
        // Persist for next visit, but DON'T bubble into parent state.
        // Lifting the handle here would re-render FormView, unskip the
        // formDocumentResult query, enter its loading branch, and
        // unmount FilledForm mid-keystroke — taking the user's just-
        // typed values with it. The autosave loop reads its handle
        // from a ref that ensureDraftHandle updates locally.
        saveDraftHandle(slug, h);
      }}
      onSubmitted={() => {
        // Drop the localStorage entry so a hard refresh starts a
        // fresh form. The in-memory `draftHandle` is intentionally
        // left alone — clearing it would make the parent re-render
        // down the publicForm path, which briefly enters a loading
        // state and unmounts FilledForm, taking the `justSubmitted`
        // flag with it. Refresh re-reads localStorage and lands on
        // a blank form as intended.
        clearDraftHandle(slug);
      }}
    />
  );
};

interface FilledFormProps {
  slug: string;
  formName: string;
  content: EacoDocumentContent;
  /** Verified owner password — replayed on startFormDraft (the server
   *  re-checks; a resumed draft doesn't need it). */
  publicPassword: string | null;
  requireTurnstile: boolean;
  requiredOverride: boolean;
  /** Answerable element type ids for the requiredOverride check. */
  answerableTypeIds: ReadonlySet<string> | undefined;
  resumedResult: FormDocumentResult | null;
  draftHandle: DraftHandle | null;
  onDraftStarted: (handle: DraftHandle) => void;
  onSubmitted: () => void;
}

const FilledForm: React.FC<FilledFormProps> = ({
  slug,
  formName,
  content,
  publicPassword,
  requireTurnstile,
  requiredOverride,
  answerableTypeIds,
  resumedResult,
  draftHandle,
  onDraftStarted,
  onSubmitted,
}) => {
  const initialValues = useMemo(
    () => (resumedResult?.responses as Record<string, unknown> | null) ?? {},
    [resumedResult]
  );

  // Local filler answers; primed with whatever the resumed draft had.
  const [values, setValues] = useImmer<Record<string, unknown>>(initialValues);
  // Whether the designer chose to show the filler their score: on the
  // result the draft start returns, or the resumed one.
  const [showLiveScore, setShowLiveScore] = useState<boolean>(
    !!resumedResult?.submission?.showLiveScore
  );

  // Required-field validation runs continuously off the current
  // values so the red bars always reflect "this required field is
  // not yet satisfied" — not just after the user pressed Submit.
  // The focusElementId / focusToken pair drives scrolling + Pages
  // jumps; those are still only updated on a failed submit attempt.
  const validation = useMemo(
    () =>
      validateDocument(content.elements, values, {
        requiredOverride,
        answerableTypeIds,
        logic: content.logic,
      }),
    [content.elements, content.logic, values, requiredOverride, answerableTypeIds]
  );
  const [focusElementId, setFocusElementId] = useState<string | null>(null);
  const [focusToken, setFocusToken] = useState(0);
  // Red "invalid" rails fill only after a failed submit; the live
  // required-but-empty set drives the amber "to-do" rails instead.
  const [submitInvalidIds, setSubmitInvalidIds] = useState<Set<string>>(
    () => new Set()
  );

  // Tracks which keys have changed since the last successful save so
  // we can flush a minimal patch on the next autosave cycle.
  const pendingPatchRef = useRef<Record<string, unknown>>({});
  const lastSavedRef = useRef<Record<string, unknown>>({ ...initialValues });

  // The draft handle the fill is actively writing into. Seeded from the
  // resumed handle and updated in place when a draft is created lazily, so the
  // FormFillContext below exposes the result id WITHOUT remounting this
  // component. (onDraftStarted deliberately keeps the handle out of FormView's
  // state to avoid a remount that would drop the user's in-progress answers;
  // this FilledForm-local state instead surfaces the live id to the file /
  // image / signature elements within the same mount, so their uploads no
  // longer sit behind "Form is still starting up" until a page reload.)
  const [liveHandle, setLiveHandle] = useState<DraftHandle | null>(draftHandle);

  const handleRef = useRef<DraftHandle | null>(draftHandle);
  useEffect(() => {
    handleRef.current = draftHandle;
  }, [draftHandle]);

  const [startDraft, { loading: starting }] = useMutation(START_FORM_DRAFT);
  const [saveDraft] = useMutation(SAVE_FORM_DRAFT);
  const [submitResult, { loading: submitting }] =
    useMutation(SUBMIT_FORM_RESULT);

  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [justSubmitted, setJustSubmitted] = useState(false);

  // Cloudflare Turnstile token — only collected when the form opts in
  // and a site key is configured for this deploy. The widget renders
  // up top; we hold onto the latest token so the first draft creation
  // can spend it. Tokens are single-use server-side per Cloudflare's
  // contract, so we mint a fresh one if the user retries.
  const turnstileEnabled = requireTurnstile && !!APP_TURNSTILE_SITE_KEY;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Honeypot. Always rendered, never shown — bots that auto-fill all
  // visible inputs will populate it and ISM will silently drop their
  // submission. Humans never see this and the value stays empty.
  const [honeypotValue, setHoneypotValue] = useState("");

  // Dedupe concurrent draft creation. Autosave, submit, and the first file
  // upload can each call ensureDraftHandle within the same tick; without a
  // shared in-flight promise they would fire their own startDraft and mint
  // duplicate draft rows. Cleared on settle so a failed attempt can retry.
  const draftPromiseRef = useRef<Promise<DraftHandle | null> | null>(null);

  // Lazy draft creation: first time the filler touches anything (or starts an
  // upload), we create the FormDocumentResult and stash its handle. This
  // avoids littering the DB with empty drafts from drive-by visitors.
  const ensureDraftHandle =
    useCallback(async (): Promise<DraftHandle | null> => {
      if (handleRef.current) return handleRef.current;
      // Gate on Turnstile: if the form requires it and we don't have a
      // token yet, abort silently — the widget will resolve later and
      // the next user interaction will re-fire this.
      if (turnstileEnabled && !turnstileToken) {
        return null;
      }
      if (draftPromiseRef.current) return draftPromiseRef.current;
      const promise = (async (): Promise<DraftHandle | null> => {
        try {
          const { data } = await startDraft({
            variables: {
              input: {
                publicSlug: slug,
                publicPassword: publicPassword || undefined,
                turnstileToken: turnstileToken || undefined,
                honeypotValue: honeypotValue || undefined,
              },
            },
          });
          const payload = data?.startFormDraft;
          if (!payload?.success || !payload.result || !payload.secretToken) {
            toast.error(
              payload?.message || "Could not start a draft for this form"
            );
            return null;
          }
          const handle: DraftHandle = {
            resultId: payload.result.id,
            secretToken: payload.secretToken,
          };
          if (payload.result.submission?.showLiveScore) setShowLiveScore(true);
          handleRef.current = handle;
          // Surface the live id to FormFillContext now, before the upload's
          // createPresignedUpload / setValue path runs, so the post-upload
          // fetchResultFileUrl (which reads the id from context) can resolve the
          // uploaded file for display.
          setLiveHandle(handle);
          onDraftStarted(handle);
          return handle;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          toast.error(`Could not start a draft: ${msg}`);
          return null;
        } finally {
          draftPromiseRef.current = null;
        }
      })();
      draftPromiseRef.current = promise;
      return promise;
    }, [
      slug,
      startDraft,
      onDraftStarted,
      turnstileEnabled,
      turnstileToken,
      honeypotValue,
      publicPassword,
    ]);

  // useImmer's Updater<T> already handles both recipe-mutator and
  // return-new-value styles; DocumentContext.setAtPath uses the
  // latter via the pure setValueAtPath helper. The diff-collector
  // runs separately in a useEffect watching `values`, so we don't
  // need to intercept writes here.
  const updateValues = setValues;

  // Diff `values` against last-saved snapshot whenever it changes; any
  // top-level key whose JSON serialisation differs goes into the
  // pending patch.
  useEffect(() => {
    const last = lastSavedRef.current;
    Object.keys(values).forEach(k => {
      if (JSON.stringify(values[k]) !== JSON.stringify(last[k])) {
        pendingPatchRef.current[k] = values[k];
      }
    });
    // Note: removing a key entirely from `values` isn't expressible
    // through this patch shape (we'd need a null/sentinel). Element
    // components clear by writing an empty string / empty array, not
    // by deleting, so this is fine in practice.
  }, [values]);

  // Debounced autosave loop. Each `values` change resets the timer;
  // when it fires we lazy-start the draft (if needed) and POST only
  // the pending patch.
  useEffect(() => {
    const hasPending = Object.keys(pendingPatchRef.current).length > 0;
    if (!hasPending) return;
    const timeout = window.setTimeout(async () => {
      const handle = await ensureDraftHandle();
      if (!handle) return;
      const patch = pendingPatchRef.current;
      if (Object.keys(patch).length === 0) return;
      pendingPatchRef.current = {};
      setSaveState("saving");
      try {
        const { data } = await saveDraft({
          variables: {
            input: {
              id: handle.resultId,
              secretToken: handle.secretToken,
              patch,
            },
          },
        });
        if (!data?.saveFormDraft?.success) {
          // Reinstate the patch keys for the next attempt; otherwise
          // we'd silently lose the unsaved keystrokes.
          Object.assign(pendingPatchRef.current, patch);
          setSaveState("error");
          toast.error(data?.saveFormDraft?.message || "Autosave failed");
          return;
        }
        // Roll the last-saved snapshot forward for the merged keys.
        Object.entries(patch).forEach(([k, v]) => {
          lastSavedRef.current[k] = v;
        });
        setSaveState("saved");
      } catch (err) {
        Object.assign(pendingPatchRef.current, patch);
        setSaveState("error");
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Autosave failed: ${msg}`);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [values, ensureDraftHandle, saveDraft]);

  const handleSubmit = useCallback(async () => {
    // Validation is already kept current via useMemo; on submit just
    // re-read it. If anything's incomplete, scroll to the first
    // failing element + jump Pages, and abort the submit.
    if (validation.invalidIds.length > 0) {
      setSubmitInvalidIds(validation.invalidIdSet);
      setFocusElementId(validation.firstInvalidId);
      setFocusToken(n => n + 1);
      toast.error(
        validation.invalidIds.length === 1
          ? "Please complete the highlighted required field"
          : `Please complete ${validation.invalidIds.length} required fields`
      );
      return;
    }
    if (focusElementId !== null) {
      setFocusElementId(null);
    }
    const handle = await ensureDraftHandle();
    if (!handle) return;
    // Flush whatever's still pending so the submit's optional patch
    // closes any open keystrokes the debounced autosave hadn't caught.
    const patch = pendingPatchRef.current;
    pendingPatchRef.current = {};
    try {
      const { data } = await submitResult({
        variables: {
          input: {
            id: handle.resultId,
            secretToken: handle.secretToken,
            patch: Object.keys(patch).length ? patch : null,
          },
        },
      });
      if (!data?.submitFormResult?.success) {
        Object.assign(pendingPatchRef.current, patch);
        toast.error(data?.submitFormResult?.message || "Submit failed");
        return;
      }
      // Drop the draft handle so a future browser visit starts a
      // fresh form (no resumed-draft path). The "submitted" screen
      // we show next is a one-shot in-memory flag — the user can't
      // navigate back to a submitted state on reload.
      onSubmitted();
      setJustSubmitted(true);
    } catch (err) {
      Object.assign(pendingPatchRef.current, patch);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Submit failed: ${msg}`);
    }
  }, [
    validation,
    focusElementId,
    ensureDraftHandle,
    submitResult,
    onSubmitted,
  ]);

  if (justSubmitted) {
    return <SubmissionCompleteView name={formName} />;
  }

  // A top-level Pages element owns its own Back / Next / Submit
  // footer (stepper UI), so suppress the standalone Submit button
  // below the form in that case to avoid two submit affordances.
  const hasTopLevelPages = (content.elements || []).some(
    el => el.typeId === "pages"
  );

  return (
    <PreviewFormProvider
      onSubmit={handleSubmit}
      submitting={submitting}
      statusSlot={
        <div className="flex items-center gap-3">
          {showLiveScore && <LiveScoreChip content={content} values={values} />}
          <SaveIndicator state={saveState} starting={starting} />
        </div>
      }
      submitSlot={
        <TurnstileSubmit
          enabled={turnstileEnabled}
          siteKey={APP_TURNSTILE_SITE_KEY || ""}
          token={turnstileToken}
          onToken={setTurnstileToken}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      }
    >
      <ValidationProvider
        invalidIds={submitInvalidIds}
        incompleteElementIds={validation.invalidIdSet}
        focusElementId={focusElementId}
        focusToken={focusToken}
        pageJumps={validation.pageJumps}
      >
        {/* Framed like the designer's desktop preview: a white "paper" card
            (rounded, bordered, shadowed) on a light-grey backdrop, with the
            form scrolling inside so the Pages stepper/footer stay in-card. */}
        {/* On a phone the card IS the screen: no grey margin, no rounded
            corners, the real viewport height (dvh) so the pinned submit bar
            sits above the browser chrome. From sm up it becomes the framed
            paper card on the grey backdrop. */}
        <div className="flex h-[100dvh] flex-col bg-white sm:bg-[#eef1f4]">
          <div className="mx-auto flex min-h-0 w-full max-w-[1000px] flex-1 flex-col gap-0 pb-0 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)] sm:gap-3 sm:pb-6 sm:pl-[max(24px,env(safe-area-inset-left))] sm:pr-[max(24px,env(safe-area-inset-right))] sm:pt-[max(24px,env(safe-area-inset-top))]">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white sm:rounded-2xl sm:border sm:border-[#e6e9ee] sm:shadow-[0_16px_38px_-16px_rgba(16,24,40,.22)]">
              <FormFillContextProvider
                publicSlug={slug}
                formDocumentResultId={liveHandle?.resultId ?? null}
                secretToken={liveHandle?.secretToken ?? null}
              >
                {/* The fill frame (paged Pages, or PagelessFillFrame) owns the
                    whole card interior now: brand (rail on desktop / band on
                    narrow), internal scroll, and the submit footer. The host
                    just supplies the fixed-height card + the autosave/Turnstile
                    widgets via the PreviewFormProvider slots above. */}
                <FormsFileTransportProvider ensureDraft={ensureDraftHandle}>
                  <div className="flex min-h-0 flex-1 flex-col">
                    {hasTopLevelPages ? (
                      <DocumentView
                        eacoDocument={content}
                        values={values}
                        updateValues={updateValues}
                      />
                    ) : (
                      <PagelessFillFrame
                        eacoDocument={content}
                        values={values}
                        updateValues={updateValues}
                      />
                    )}
                  </div>
                </FormsFileTransportProvider>
                {/* Honeypot. Always rendered, hidden from humans via
                    off-screen positioning rather than display:none so
                    naive bots that skip display:none fields still see
                    + auto-fill it. Bots auto-filling all visible inputs
                    will populate `website`; ISM detects the non-empty
                    value and quietly drops the submission. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={honeypotValue}
                  onChange={e => setHoneypotValue(e.target.value)}
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    width: "1px",
                    height: "1px",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                />
              </FormFillContextProvider>
            </div>
            <div className="hidden sm:block">
              <PoweredByEaco />
            </div>
          </div>
        </div>
      </ValidationProvider>
    </PreviewFormProvider>
  );
};

/**
 * The autosave state as a small chip: a spinner while a save is in flight, a
 * green tick with "Saved" that settles to the tick alone after a moment, and
 * a red "Not saved" when a save failed (the toast carries the detail). Quiet
 * enough to live in the brand band on a phone, legible enough to trust.
 */
const SaveIndicator: React.FC<{
  state: "idle" | "saving" | "saved" | "error";
  starting: boolean;
}> = ({ state, starting }) => {
  const busy = starting || state === "saving";
  // "Saved" reads for a moment after each save, then only the tick stays.
  const [recent, setRecent] = useState(false);
  useEffect(() => {
    if (state !== "saved") return;
    setRecent(true);
    const t = setTimeout(() => setRecent(false), 2400);
    return () => clearTimeout(t);
  }, [state]);
  if (!busy && state !== "saved" && state !== "error") return null;

  const tone = busy
    ? { color: "#5b6b82", background: "#f1f4f8" }
    : state === "error"
      ? { color: "#b42318", background: "#fdecea" }
      : { color: "#0f7b3f", background: "#e6f6ec" };
  const label = busy ? "Saving" : state === "error" ? "Not saved" : "Saved";
  const showLabel = busy || state === "error" || recent;

  return (
    <span
      role="status"
      aria-live="polite"
      title={
        busy
          ? "Saving your answers"
          : state === "error"
            ? "Your last change could not be saved. Check your connection; it saves again with your next change."
            : "All changes saved"
      }
      className={cn(
        "inline-flex h-[24px] items-center rounded-full text-[11.5px] font-bold tabular-nums transition-[background-color,color,padding] duration-200",
        showLabel ? "gap-[6px] pl-[6px] pr-[9px]" : "w-[24px] justify-center gap-0 px-0",
      )}
      style={tone}
    >
      {busy ? (
        <svg
          viewBox="0 0 16 16"
          className="h-[13px] w-[13px] animate-spin"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M8 2.5a5.5 5.5 0 1 1-5.5 5.5" />
        </svg>
      ) : state === "error" ? (
        <svg
          viewBox="0 0 16 16"
          className="h-[13px] w-[13px]"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M8 4.5v4M8 11.2v.3" />
          <circle cx="8" cy="8" r="6" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 16 16"
          className="h-[13px] w-[13px]"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3.5 8.5l2.8 2.8L12.5 5" />
        </svg>
      )}
      <span
        className="overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-300"
        style={{ maxWidth: showLabel ? 80 : 0, opacity: showLabel ? 1 : 0 }}
      >
        {label}
      </span>
    </span>
  );
};

const AlreadySubmittedView: React.FC<{ name: string }> = ({ name }) => (
  <SuccessShell title={name || "Form submitted"} />
);

/**
 * In-memory submission-complete screen shown immediately after a
 * successful submit. The draft handle has already been cleared from
 * localStorage by that point, so a hard refresh will land the user
 * back on a fresh form rather than this screen.
 */
const SubmissionCompleteView: React.FC<{ name: string }> = ({ name }) => (
  <SuccessShell title={name || "Form submitted"} subtitle="Submitted" />
);

const SuccessShell: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div className="flex min-h-screen flex-col bg-background">
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="max-w-md w-full space-y-4 text-center">
        <div
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: "#2B7AEA1a" }}
        >
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="h-10 w-10"
            style={{ color: "#2B7AEA" }}
          />
        </div>
        {subtitle && (
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {subtitle}
          </p>
        )}
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">
          Thanks — your response has been recorded. You can safely close this
          tab.
        </p>
      </div>
    </div>
    <PoweredByEaco />
  </div>
);

export default FormView;
